import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import {
    updateCurrentAdminProfile,
    requestCurrentAdminEmailChange,
    confirmCurrentAdminEmailChange,
    requestCurrentAdminPasswordChange,
    confirmCurrentAdminPasswordChange,
    sendCurrentAdminEmailVerification,
    confirmCurrentAdminEmailVerification,
} from "../../actions/auth.actions";
import { getRegions } from "../../actions";

const CODE_TTL_SECONDS = 300;

const getTitle = (type) => {
    if (type === "personal") return "Личные данные";
    if (type === "contact") return "Контакты";
    if (type === "email") return "Изменить email";
    if (type === "password") return "Изменить пароль";
    if (type === "verifyEmail") return "Подтвердить email";
    return "Редактирование профиля";
};

const getSecureRandomIndex = (length) => {
    if (length <= 1) return 0;
    const cryptoApi = typeof window !== "undefined" ? window.crypto : null;
    if (cryptoApi?.getRandomValues) {
        const value = new Uint32Array(1);
        cryptoApi.getRandomValues(value);
        return value[0] % length;
    }
    return Math.floor(Math.random() * length);
};

const generatePassword = () => {
    const lower = "abcdefghijkmnopqrstuvwxyz";
    const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
    const digits = "23456789";
    const symbols = "!@#$%&*+-_=.?";
    const all = `${lower}${upper}${digits}${symbols}`;
    const pick = (pool) => pool[getSecureRandomIndex(pool.length)];
    const chars = [pick(lower), pick(upper), pick(digits), pick(symbols)];
    while (chars.length < 16) chars.push(pick(all));
    for (let index = chars.length - 1; index > 0; index -= 1) {
        const swapIndex = getSecureRandomIndex(index + 1);
        [chars[index], chars[swapIndex]] = [chars[swapIndex], chars[index]];
    }
    return chars.join("");
};

const getRegionId = (region) => {
    if (!region) return "";
    if (typeof region === "object") return region._id || region.value || region.id || "";
    return region;
};

const isPhoneInputValid = (value) => {
    const raw = String(value || "").trim();

    if (!raw) return true;
    if (!/^[+\d\s()-]+$/.test(raw)) return false;

    const plusMatches = raw.match(/\+/g) || [];
    if (plusMatches.length > 1 || (plusMatches.length === 1 && !raw.startsWith("+"))) {
        return false;
    }

    const digits = raw.replace(/\D/g, "");

    if (raw.startsWith("+")) {
        return digits.length === 11 && digits.startsWith("7");
    }

    return digits.length === 10 ||
        (digits.length === 11 && (digits.startsWith("7") || digits.startsWith("8")));
};

const EditCabinetModal = ({ show, type, user = {}, handleClose, onSuccess }) => {
    const dispatch = useDispatch();
    const regionSlice = useSelector((state) => state.region);
    const regionsRaw = regionSlice?.regions || regionSlice?.items || regionSlice?.list || [];
    const regionOptions = useMemo(() => Array.isArray(regionsRaw) ? regionsRaw.map((region) => ({
        value: region?._id ?? region?.value ?? region?.id,
        name: region?.name ?? region?.label ?? region?.title ?? "Без названия",
    })).filter((option) => option.value && option.name) : [], [regionsRaw]);

    const [values, setValues] = useState({});
    const [step, setStep] = useState("request");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");
    const [secondsLeft, setSecondsLeft] = useState(0);
    const [visiblePasswords, setVisiblePasswords] = useState({});

    useEffect(() => {
        if (show && !regionsRaw.length) dispatch(getRegions());
    }, [show, dispatch, regionsRaw.length]);

    useEffect(() => {
        if (!show) return;
        setStep("request"); setLoading(false); setError(""); setMessage(""); setSecondsLeft(0); setVisiblePasswords({});
        setValues({
            firstName: user.firstName || "", lastName: user.lastName || "", birthData: user.birthData || "", gender: user.gender || "",
            region: getRegionId(user.region), contactNumber: user.contactNumber || "", backupNumber: user.backupNumber || "",
            email: user.email || "", currentPassword: "", newPassword: "", confirmPassword: "", code: "",
        });
    }, [show, type, user]);

    useEffect(() => {
        if (!show || secondsLeft <= 0) return undefined;
        const timer = setInterval(() => setSecondsLeft((current) => Math.max(current - 1, 0)), 1000);
        return () => clearInterval(timer);
    }, [show, secondsLeft]);

    useEffect(() => {
        if (!show) return undefined;
        const onKeyDown = (event) => { if (event.key === "Escape" && !loading) handleClose?.(); };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [show, loading, handleClose]);

    if (!show || typeof document === "undefined") return null;

    const setField = (key, value) => { setValues((current) => ({ ...current, [key]: value })); setError(""); setMessage(""); };
    const run = async (handler) => {
        setLoading(true); setError(""); setMessage("");
        try {
            const result = await handler();
            if (!result?.ok) { setError(result?.message || "Ошибка выполнения операции"); return result; }
            if (result?.message) setMessage(result.message);
            return result;
        } catch (requestError) {
            const text = requestError?.message || "Ошибка выполнения операции"; setError(text); return { ok: false, message: text };
        } finally { setLoading(false); }
    };

    const validate = () => {
        if (type === "personal") {
            if (String(values.firstName || "").trim().length < 3) return "Введите имя минимум из 3 символов";
            if (String(values.lastName || "").trim().length < 3) return "Введите фамилию минимум из 3 символов";
        }
        if (type === "contact") {
            if (!isPhoneInputValid(values.contactNumber)) return "Основной телефон укажите в формате +7 999 123-45-67";
            if (!isPhoneInputValid(values.backupNumber)) return "Резервный телефон укажите в формате +7 999 123-45-67";
        }
        if (type === "email" && step === "request") {
            const email = String(values.email || "").trim();
            if (!email) return "Введите новый email";
            if (!/^\S+@\S+\.\S+$/.test(email)) return "Email указан неверно";
            if (email.toLowerCase() === String(user.email || "").toLowerCase()) return "Укажите другой email";
        }
        if ((type === "email" || type === "password" || type === "verifyEmail") && step === "confirm" && !/^\d{6}$/.test(String(values.code || "").trim())) return "Введите 6-значный код";
        if (type === "password" && step === "request") {
            if (!values.currentPassword) return "Введите текущий пароль";
            if (!values.newPassword || values.newPassword.length < 6) return "Новый пароль должен быть минимум 6 символов";
            if (values.currentPassword === values.newPassword) return "Новый пароль должен отличаться от текущего";
            if (values.newPassword !== values.confirmPassword) return "Новые пароли не совпадают";
        }
        return "";
    };

    const submit = async (event) => {
        event.preventDefault();
        const validationError = validate(); if (validationError) { setError(validationError); return; }
        if (type === "personal" || type === "contact") {
            const fields = type === "personal"
                ? { firstName: values.firstName, lastName: values.lastName, birthData: values.birthData, gender: values.gender, region: values.region }
                : { contactNumber: values.contactNumber, backupNumber: values.backupNumber };
            const result = await run(() => dispatch(updateCurrentAdminProfile(type, fields))); if (result?.ok) onSuccess?.(result); return;
        }
        if (type === "verifyEmail") {
            if (step === "request") {
                const result = await run(() => dispatch(sendCurrentAdminEmailVerification()));
                if (result?.ok) { if (result?.user?.emailVerified || result?.user?.isActivated) { onSuccess?.(result); return; } setStep("confirm"); setSecondsLeft(CODE_TTL_SECONDS); setField("code", ""); }
                return;
            }
            const result = await run(() => dispatch(confirmCurrentAdminEmailVerification(values.code))); if (result?.ok) onSuccess?.(result); return;
        }
        if (type === "email") {
            if (step === "request") {
                const result = await run(() => dispatch(requestCurrentAdminEmailChange(values.email))); if (result?.ok) { setStep("confirm"); setSecondsLeft(CODE_TTL_SECONDS); setField("code", ""); } return;
            }
            const result = await run(() => dispatch(confirmCurrentAdminEmailChange(values.code))); if (result?.ok) onSuccess?.(result); return;
        }
        if (type === "password") {
            if (step === "request") {
                const result = await run(() => dispatch(requestCurrentAdminPasswordChange({ currentPassword: values.currentPassword, newPassword: values.newPassword, confirmPassword: values.confirmPassword })));
                if (result?.ok) { setStep("confirm"); setSecondsLeft(CODE_TTL_SECONDS); setField("code", ""); } return;
            }
            const result = await run(() => dispatch(confirmCurrentAdminPasswordChange(values.code))); if (result?.ok) onSuccess?.(result);
        }
    };

    const resend = async () => {
        if (secondsLeft > CODE_TTL_SECONDS - 60) return;
        if (type === "verifyEmail") { const result = await run(() => dispatch(sendCurrentAdminEmailVerification())); if (result?.ok) setSecondsLeft(CODE_TTL_SECONDS); }
        if (type === "email") { const result = await run(() => dispatch(requestCurrentAdminEmailChange(values.email))); if (result?.ok) setSecondsLeft(CODE_TTL_SECONDS); }
        if (type === "password") { const result = await run(() => dispatch(requestCurrentAdminPasswordChange({ currentPassword: values.currentPassword, newPassword: values.newPassword, confirmPassword: values.confirmPassword }))); if (result?.ok) setSecondsLeft(CODE_TTL_SECONDS); }
    };

    const renderInput = (key, label, inputType = "text", options = {}) => (
        <label className={`cabinetEditModal__field${options.full ? " is-full" : ""}`}>
            <span>{label}</span>
            {inputType === "select" ? (
                <select value={values[key] || ""} disabled={loading || options.disabled} onChange={(event) => setField(key, event.target.value)}>
                    <option value="">{options.placeholder || "Не указан"}</option>
                    {(options.options || []).map((option) => <option key={option.value} value={option.value}>{option.name}</option>)}
                </select>
            ) : (
                <input type={inputType} value={values[key] || ""} disabled={loading || options.disabled} placeholder={options.placeholder || ""} autoComplete={options.autoComplete || "off"}
                    onChange={(event) => setField(key, key === "code" ? event.target.value.replace(/\D/g, "").slice(0, 6) : event.target.value)} />
            )}
        </label>
    );

    const renderPasswordInput = (key, label, allowGenerate = false) => {
        const visible = !!visiblePasswords[key];
        return <label className="cabinetEditModal__field"><span>{label}</span><div className="cabinetEditModal__passwordWrap">
            <input type={visible ? "text" : "password"} value={values[key] || ""} disabled={loading} autoComplete={key === "currentPassword" ? "current-password" : "new-password"} onChange={(event) => setField(key, event.target.value)} />
            <div className="cabinetEditModal__passwordActions">
                {allowGenerate ? <button type="button" onClick={() => { const generated = generatePassword(); setValues((current) => ({ ...current, newPassword: generated, confirmPassword: generated })); setVisiblePasswords((current) => ({ ...current, newPassword: true, confirmPassword: true })); setMessage("Новый пароль сгенерирован и продублирован в подтверждение."); }} title="Сгенерировать пароль"><AutoAwesomeOutlinedIcon sx={{ fontSize: 19 }} /></button> : null}
                <button type="button" onClick={() => setVisiblePasswords((current) => ({ ...current, [key]: !current[key] }))} title={visible ? "Скрыть пароль" : "Показать пароль"}>{visible ? <VisibilityOffOutlinedIcon sx={{ fontSize: 20 }} /> : <VisibilityOutlinedIcon sx={{ fontSize: 20 }} />}</button>
            </div></div></label>;
    };

    const genderOptions = [{ value: "male", name: "Мужской" }, { value: "female", name: "Женский" }];
    const minutes = String(Math.floor(secondsLeft / 60)).padStart(2, "0");
    const seconds = String(secondsLeft % 60).padStart(2, "0");

    return createPortal(<div className="cabinetEditModal">
        <button type="button" className="cabinetEditModal__backdrop" onClick={loading ? undefined : handleClose} aria-label="Закрыть" />
        <form className="cabinetEditModal__panel" onSubmit={submit}>
            <div className="cabinetEditModal__head"><div><span>Профиль ADMIN</span><h2>{getTitle(type)}</h2></div><button type="button" onClick={handleClose} disabled={loading} aria-label="Закрыть"><CloseOutlinedIcon sx={{ fontSize: 21 }} /></button></div>
            <div className="cabinetEditModal__content">
                {type === "personal" ? <div className="cabinetEditModal__grid">
                    {renderInput("firstName", "Имя")}{renderInput("lastName", "Фамилия")}{renderInput("birthData", "Дата рождения", "date")}
                    {renderInput("gender", "Пол", "select", { placeholder: "Не указан", options: genderOptions })}
                    {renderInput("region", "Регион", "select", { placeholder: "Выберите регион", options: regionOptions, full: true })}
                </div> : null}
                {type === "contact" ? <div className="cabinetEditModal__grid">{renderInput("contactNumber", "Основной телефон", "text", { placeholder: "+7 999 123-45-67" })}{renderInput("backupNumber", "Резервный телефон", "text", { placeholder: "+7 999 123-45-67" })}</div> : null}
                {type === "verifyEmail" ? <div className="cabinetEditModal__stack"><p className="cabinetEditModal__hint">Код подтверждения будет отправлен на текущий email: <b>{user.email || "—"}</b></p>{step === "confirm" ? <>{renderInput("code", "Код подтверждения", "text", { autoComplete: "one-time-code", placeholder: "123456" })}<div className="cabinetEditModal__timer"><span>Код действует</span><b>{minutes}:{seconds}</b></div><button type="button" className="cabinetEditModal__secondaryButton" onClick={resend} disabled={loading || secondsLeft > CODE_TTL_SECONDS - 60}>Отправить код повторно</button></> : null}</div> : null}
                {type === "email" ? <div className="cabinetEditModal__stack">{renderInput("email", "Новый email", "email", { disabled: step === "confirm", autoComplete: "email", placeholder: "mail@example.com" })}{step === "confirm" ? <>{renderInput("code", "Код подтверждения", "text", { autoComplete: "one-time-code", placeholder: "123456" })}<div className="cabinetEditModal__timer"><span>Код действует</span><b>{minutes}:{seconds}</b></div><button type="button" className="cabinetEditModal__secondaryButton" onClick={resend} disabled={loading || secondsLeft > CODE_TTL_SECONDS - 60}>Отправить код повторно</button></> : <p className="cabinetEditModal__hint">Код подтверждения будет отправлен на новый email.</p>}</div> : null}
                {type === "password" ? <div className="cabinetEditModal__stack">{step === "request" ? <>{renderPasswordInput("currentPassword", "Текущий пароль")}{renderPasswordInput("newPassword", "Новый пароль", true)}{renderPasswordInput("confirmPassword", "Повторите новый пароль")}</> : <>{renderInput("code", "Код подтверждения", "text", { autoComplete: "one-time-code", placeholder: "123456" })}<div className="cabinetEditModal__timer"><span>Код действует</span><b>{minutes}:{seconds}</b></div><button type="button" className="cabinetEditModal__secondaryButton" onClick={resend} disabled={loading || secondsLeft > CODE_TTL_SECONDS - 60}>Отправить код повторно</button></>}</div> : null}
                {message ? <div className="cabinetEditModal__message">{message}</div> : null}{error ? <div className="cabinetEditModal__error">{error}</div> : null}
            </div>
            <div className="cabinetEditModal__footer"><button type="button" className="is-secondary" onClick={handleClose} disabled={loading}>Отмена</button><button type="submit" disabled={loading}>{step === "confirm" ? <CheckCircleOutlineOutlinedIcon sx={{ fontSize: 18 }} /> : <SendOutlinedIcon sx={{ fontSize: 18 }} />}<span>{loading ? "Обработка..." : step === "confirm" ? "Подтвердить" : (type === "email" || type === "password" || type === "verifyEmail" ? "Отправить код" : "Сохранить")}</span></button></div>
        </form>
    </div>, document.body);
};

export default EditCabinetModal;
